export type BirthdayRoom = {
  id: string
  questName: string
  name: string
  image: string
  capacity: number
  minAge: number
}

export type BirthdayCatalogItem = {
  id: string
  name: string
  price: number
  image?: string
  servings?: number
  unit?: string
  category?: string
  contents?: string[]
}

export type BirthdayCatalog = {
  version: string
  currency: 'CZK'
  timezone: 'Europe/Prague'
  rooms: BirthdayRoom[]
  packages: BirthdayCatalogItem[]
  extras: BirthdayCatalogItem[]
  cakes: BirthdayCatalogItem[]
  decor: BirthdayCatalogItem[]
  pizzaFlavors: Array<{ id: string; name: string }>
  services: {
    ownCake: { price: number }
    candles: { price: number }
  }
  drinks: {
    includedInEstimate: false
    menuUrl: string
  }
}

export const BIRTHDAY_PARTY_STARTS = [
  '09:30',
  '11:00',
  '12:30',
  '14:00',
  '15:30',
  '17:00',
  '18:30',
] as const

export type AvailableBirthdayRoom = {
  roomId: string
  questName: string
  name?: string
  capacity?: number
  available: boolean
  reason: string | null
  standardStart?: string
  standardEnd?: string
  gameStart?: string
  gameEnd?: string
}

export type BirthdayAvailability = {
  date: string
  partyStart: string
  rooms: AvailableBirthdayRoom[]
}

export type BirthdayAvailabilityResponse = {
  date: string
  availability: Record<string, BirthdayAvailability>
}

export type BirthdayQuote = {
  currency: 'CZK'
  catalogVersion: string
  games: number
  services: number
  prepayment: number
  onsite: number
  fixedTotal: number
  drinksIncluded: false
  packageCount: number
  recommendedPackages: number
  validation: Array<{ code: string; field: string }>
}

export type BirthdayQuoteRequest = {
  date: string
  start: string
  selectionMode: BirthdaySelectionMode
  duration: number
  children: number
  parents: number
  age: number
  selectedRooms: string[]
  sessions: BirthdaySession[]
  assignments: Record<string, number>
  supervision: Record<string, 'parent' | 'coach' | 'none'>
  packages: Record<string, number>
  pizzaFlavors: Record<string, number>
  extras: Record<string, number>
  cake: { mode: 'none' | 'own' | 'ours'; items: Record<string, number> }
  decor: 'none' | 'mini' | 'wizard' | 'pink'
  candles: number
}

export type BirthdaySelectionMode = 'parallel' | 'staggered'

export type BirthdaySession = {
  id: string
  roomId: string
  start: string
}

export const birthdaySessionId = (roomId: string, start: string) =>
  `${roomId}@${start}`

export type BirthdayShareSelection = {
  catalogVersion?: string
  date: string
  selectionMode: BirthdaySelectionMode
  duration: number
  children: number
  parents?: number
  age: number
  sessions: BirthdaySession[]
  assignments: Record<string, number>
  supervision: Record<string, 'parent' | 'coach' | 'none'>
  packages: Record<string, number>
  pizzaFlavors: Record<string, number>
  extras: Record<string, number>
  cake: { mode: 'none' | 'own' | 'ours'; items: Record<string, number> }
  decor: 'none' | 'mini' | 'wizard' | 'pink'
  candles: number
}

export type BirthdayShareSnapshot = {
  version: 1 | 2 | 3
  createdAt: string
  expiresAt: string
  selection: BirthdayShareSelection
}

export type BirthdayShareCreated = {
  id: string
  createdAt: string
  expiresAt: string
}

export type BirthdayOrderCustomer = {
  name: string
  phone: string
  email: string
  note: string
  voucher: string
  rulesAccepted: boolean
  gdprAccepted: boolean
  locale: 'cs' | 'en'
}

export type BirthdayOrderRequest = BirthdayQuoteRequest & {
  customer: BirthdayOrderCustomer
}

export type BirthdayOrderResponse = {
  groupId: string
  color: string
  reservationIds: number[]
  fixedTotal: number
  currency: 'CZK'
  shareId: string
  selectionUrl: string
  confirmationEmailSent: boolean
  warning: 'confirmation_email_failed' | null
}

export type BirthdayCancellationSummary = {
  status: 'active' | 'cancelled'
  groupId: string
  customerName: string
  date: string
  total: number
  currency: 'CZK'
  selectionUrl: string
  cancellationEmailSent: boolean
  games: Array<{
    name: string
    time: string
    players: number
    price: number
  }>
  food: Array<{
    name: string
    quantity: number
    total: number
  }>
  services?: Array<{
    name: string
    quantity: number
    total: number
  }>
}
